import { isPlainObject } from 'es-toolkit/compat';
import { mapValues } from 'es-toolkit/compat';

type MaybeCensoredValue = { __censored: boolean; value: any };

const isMaybeCensoredValue = (v: unknown): v is MaybeCensoredValue => {
  return Boolean(v && typeof v === 'object' && '__censored' in v);
};

export class MaybeCensored<ValueType> {
  private constructor(
    private value: ValueType,
    private isCensored: boolean,
  ) {}

  public pipe<ResultType>(
    cb: (v: ValueType) => ResultType,
  ): MaybeCensored<ResultType extends MaybeCensored<infer R> ? R : ResultType> {
    if (this.isCensored) {
      return new MaybeCensored<any>(
        this.value,
        true,
      );
    }

    const mappedValue = cb(this.value);

    if (mappedValue instanceof MaybeCensored) {
      return mappedValue;
    } else {
      return new MaybeCensored<any>(mappedValue, false);
    }
  }

  public toString(): string {
    if (this.isCensored) {
      return '*****';
    }

    if (this.value === null) {
      return '';
    }
    return (this.value as any).toString();
  }

  public unwrap(): ValueType {
    if (this.isCensored) {
      throw new Error('Value is censored');
    }

    return this.value as ValueType;
  }

  public maybeUnwrap(): ValueType | undefined {
    return this.isCensored ? undefined : (this.value as ValueType);
  }

  static wrap<T>(value: T): MaybeCensoredType<T> {
    if (isMaybeCensoredValue(value)) {
      return new MaybeCensored(
        value.value,
        value.__censored,
      ) as MaybeCensoredType<T>;
    }

    if (isPlainObject(value)) {
      return mapValues(value as any, v =>
        MaybeCensored.wrap(v),
      ) as MaybeCensoredType<T>;
    } else {
      return value as MaybeCensoredType<T>;
    }
  }

  static maybeUnwrapRecursive<T>(value: any): T {
    if (isMaybeCensored(value)) {
      return value.maybeUnwrap() as T;
    }

    if (isPlainObject(value)) {
      return mapValues(value as any, v =>
        MaybeCensored.maybeUnwrapRecursive(v),
      ) as T;
    } else {
      return value as T;
    }
  }
}

export type MaybeCensoredType<ValueType> = ValueType extends {
  __censored: boolean;
  value: infer R;
}
  ? MaybeCensored<R>
  : ValueType extends { [x: string]: any }
    ? { [K in keyof ValueType]: MaybeCensoredType<ValueType[K]> }
    : ValueType;

export type RecursiveMaybeCensored<T> = {
  [K in keyof T]: T[K] extends { [x: string]: any }
    ? RecursiveMaybeCensored<T[K]>
    : MaybeCensored<T[K]>;
};

const isMaybeCensored = <ValueType>(
  value: unknown,
): value is MaybeCensored<ValueType> => {
  return Boolean(value && typeof value === 'object' && 'isCensored' in value);
};
